import React, { useState } from "react";
import { RouteComponentProps } from "react-router";
import useReactRouter from "use-react-router";
import moment from "moment";
import clsx from "clsx";
import { Button, Card, CardHeader, Divider, Grid, Link, Typography } from "@material-ui/core";
import { T } from "react-polyglot-hooks";
import PaymentCheckoutFrame from "../../Payments/PaymentCheckoutFrame";
import { FeatureContext } from "../../../App";
import { getMe } from '../../../graphql/auth';
import { FeatureFlagSlug } from "../../../graphql/features";
import { LoadingOverlay } from "../../../components/LoadingOverlay";
import { formatDate } from "../../../utils/formatters";
import { getHost } from "../../../graphql/GraphQlProvider";
import { useStyles } from "./styles";
import PaymentSchedule from "../PaymentSchedule/PaymentSchedule";
import getMyBillingParty from "./getMyBillingParty.api";
import { BillingParty } from "../../../graphql/billing_party.datatypes";
import { checkIsPrimaryPayer } from "../../../utils/checkIsPrimaryPayer";
import HowToUseSite from "../HowToUseSite";

const TripDetails = () => {
    const {
        match
    }: RouteComponentProps<{ id: string }> = useReactRouter();
    const id = parseInt(match.params.id);
    const classes = useStyles();
    const [makePaymentPartyId, setMakePaymentPartyId] = useState(-1);
    const [makePaymentMemberId, setMakePaymentMemberId] = useState(-1);

    const meData = getMe();
    const email = meData && meData.data && meData.data.me && meData.data.me.email;
    const isPrimaryPayer = checkIsPrimaryPayer();

    const billingPartyData = getMyBillingParty(id);
    const billingParty = billingPartyData && billingPartyData.data && billingPartyData.data.billingParty;
    const billingPartyMember = billingParty && billingParty.payingMembers && billingParty.payingMembers.find(member => member.email === email);

    const features = React.useContext(FeatureContext);
    const automaticPaymentsFeature = features.find(
        f => f.slug === FeatureFlagSlug.AUTOMATIC_PAYMENTS
    );
    const automaticPaymentsEnabled = (automaticPaymentsFeature && automaticPaymentsFeature.enabled) || false;

    return (
        <React.Fragment>
            <div className={classes.root}>
                <Grid container spacing={4}>
                    <Grid item lg={12} md={12} xl={12} xs={12}>
                        <Typography variant="h1">
                            <T phrase="Welcome to the WCV Portal" />
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        {(!billingPartyMember) || isPrimaryPayer && (
                            <HowToUseSite/>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <LoadingOverlay loading={billingPartyData.loading}>
                            {billingParty && (
                                <Details
                                    billingParty={billingParty}
                                    automaticPaymentsEnabled={automaticPaymentsEnabled}
                                    isPrimaryPayer={isPrimaryPayer}
                                    email={email || ""}
                                    setMakePaymentPartyId={setMakePaymentPartyId}
                                    setMakePaymentMemberId={setMakePaymentMemberId}
                                />
                            )}
                        </LoadingOverlay>

                        {makePaymentPartyId !== -1 && makePaymentMemberId && (
                            <PaymentCheckoutFrame
                                open={true}
                                billingPartyId={makePaymentPartyId}
                                billingPartyMemberId={makePaymentMemberId}
                                onClose={() => setMakePaymentPartyId(-1)}
                                onSuccess={() => setMakePaymentPartyId(-1)}
                            />
                        )}
                    </Grid>
                </Grid>
            </div>
        </React.Fragment>
    );
};

export default TripDetails;

const Details = (props: {
    billingParty: BillingParty;
    automaticPaymentsEnabled: boolean;
    isPrimaryPayer: boolean;
    email: string;
    setMakePaymentPartyId: (id: number) => void;
    setMakePaymentMemberId: (id: number) => void;
}) => {
    const { billingParty, automaticPaymentsEnabled, isPrimaryPayer, email, setMakePaymentPartyId, setMakePaymentMemberId } = props;
    const { history } = useReactRouter();
    const classes = useStyles();
    const startDate = moment(billingParty.booking!.start_date);
    const endDate = moment(billingParty.booking!.end_date);
    const bookingLength = endDate.diff(startDate, 'days');
    const billingPartyMember = billingParty && billingParty.payingMembers && billingParty.payingMembers.find(member => member.email === email);
    const billingPartyId = billingParty ? billingParty.id : -1;
    const outstandingBalance = billingParty.outstandingBalance || 0;

    return (
        <React.Fragment>
            <Card className={clsx(classes.root, classes.marginBottom)}>
                <CardHeader title="My Stay" />
                <Divider />

                <Grid container spacing={1}>
                    <Grid item xs={12} className={clsx(classes.datesBlock, classes.constrainedContent)}>
                        <Grid container spacing={1}>
                            <Grid item xs={12} className={clsx([classes.nowrap])}>
                                <strong>Event:</strong> {billingParty.booking!.group!.tour!.display_name}
                            </Grid>
                            <Grid item xs={12} className={clsx([classes.nowrap, classes.datesBlockDisclosure])}>
                                <strong>Group:</strong> {billingParty.booking!.group!.name}
                            </Grid>
                            <Grid item xs={12} className={clsx([classes.nowrap, classes.datesBlockDisclosure])}>
                                <strong>Hotel:</strong> {billingParty.booking!.group!.hotel!.display_name}
                            </Grid>
                            <Grid item xs={6} className={clsx([classes.nowrap, classes.datesBlockCheckIn])}>
                                <strong>Check In:</strong> {formatDate(startDate)}
                            </Grid>
                            <Grid item xs={6} className={clsx([classes.nowrap, classes.datesBlockCheckOut])}>
                                <strong>Check Out:</strong> {formatDate(endDate)}
                            </Grid>
                            <Grid item xs={12} className={classes.nowrap}>
                                <strong>Duration:</strong> {bookingLength}-Nights
                            </Grid>
                            <Grid item xs={12} className={classes.datesBlockDisclosure}>
                                <strong>Arrival/Departure Information:</strong><br />
                                {billingParty.booking!.group!.description_checkin}
                            </Grid>
                            <Grid item xs={12} className={clsx([classes.datesBlockDisclosure, classes.variedDatesMessage])}>
                                The above hotel dates reflect the entire length a room is registered for. If any members
                                in your room are leaving earlier/later than the rest, please click 'Download Invoice'
                                below to see costs and duration per guest. These are the actual dates and costs that you
                                are being billed for. <br />
                                NOTE: If sharing a room with another family and they are being
                                billed separately you will not see their options. They would need to refer to their
                                account for what they are being billed for.
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Card>

            <Card className={clsx(classes.root, classes.marginBottom)}>
                <Grid container spacing={1} className={classes.marinBlock}>
                    <Grid item xs={12}>
                        <Link href={getHost() + '/invoice/' + billingParty.code}>
                            <Button variant="contained">Download Invoice</Button>
                        </Link>
                        <Button onClick={() => {
                            setMakePaymentPartyId(billingPartyId);
                            if (billingPartyMember) setMakePaymentMemberId(billingPartyMember.id);
                        }} variant="contained" className={classes.linkButton} disabled={outstandingBalance <= 0}>Make Payment</Button>

                        {isPrimaryPayer &&
                            <Button onClick={() => history.push('/booking/' + billingParty.booking!.group!.booking_form_url + '/' + billingParty.booking!.id)} variant="contained" className={classes.linkButton}>Manage My Trip</Button>
                        }

                        {automaticPaymentsEnabled && isPrimaryPayer && (
                            <Button
                                onClick={() =>
                                    history.push(
                                        '/mygroups/billing_party/' +
                                        btoa(
                                            JSON.stringify({
                                                id: billingParty.id,
                                                groupRef: billingParty.booking!.group!.booking_form_url,
                                            })
                                        )
                                    )
                                }
                                variant="contained"
                                className={classes.linkButton}
                            >
                                Manage Automatic Payments
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Card>

            <PaymentSchedule billingPartyId={billingPartyId} className={classes.marginBottom} />
        </React.Fragment>
    );
}
